import { useCallback, useEffect, useMemo, useRef } from 'react'

import classNames from 'classnames'

import { useTranslation } from 'react-i18next'

import {
  useAppDispatch,
  useConversationActorRole,
  usePublishedListings,
  useUserCulture,
} from '@app/hooks'
import {
  trackReplySelectSearchUsed,
  trackReplySelectSortingChanged,
  trackReplySelectTransactionTypeChanged,
} from '@app/services/tracking'
import { getPublishedListingsThunk } from '@app/store/publishedListings'

import {
  ListingCard,
  ListingsSorting,
  Loader,
  Search,
  SortingDropdown,
  TransactionTypeDropdown,
  TRANSACTION_TYPE_QUERY,
  useListingCard,
} from '@lib/components'
import type { TransactionType } from '@lib/components'

import { getNumberFormatted } from '@lib/utils'

import { SEARCH_PLACEHOLDER } from './constants'
import styles from './styles.module.scss'
import { SelectListingContainerProps } from './types'
import { useEntityInfiniteScroll } from './useEntityInfiniteScroll'
import { useScrollbarPresence } from './useScrollbarPresence'

export const SelectListingContainer = ({
  onSelect,
  searchQuery,
  onSearchChange,
  transactionType,
  onTransactionTypeChange,
  sort,
  onSortChange,
  selectedIds = [],
}: SelectListingContainerProps) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const conversationActorRole = useConversationActorRole()

  const { listings, fetching, loaded, hasMore, skip, pageSize, total } =
    usePublishedListings()
  const culture = useUserCulture()
  const { getListingCardData } = useListingCard()
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const hasScrollBar = useScrollbarPresence(scrollContainerRef, [listings, fetching])
  const transactionTypeQueryValue = transactionType
    ? TRANSACTION_TYPE_QUERY[transactionType]
    : undefined
  const skipNextSortFetchRef = useRef(false)
  const lastTrackedSearchValueRef = useRef(searchQuery)
  const totalListings = total ?? 0

  useEffect(() => {
    lastTrackedSearchValueRef.current = searchQuery
  }, [searchQuery])

  useEffect(() => {
    if (skipNextSortFetchRef.current) {
      skipNextSortFetchRef.current = false
      return
    }

    dispatch(
      getPublishedListingsThunk({
        count: pageSize,
        search: searchQuery || undefined,
        transactionType: transactionTypeQueryValue,
        sort,
      })
    )
  }, [dispatch, pageSize, searchQuery, sort, transactionTypeQueryValue])

  const loadPage = useCallback(
    (nextSkip: number) => {
      dispatch(
        getPublishedListingsThunk({
          skip: nextSkip,
          count: pageSize,
          search: searchQuery || undefined,
          transactionType: transactionTypeQueryValue,
          sort,
        })
      )
    },
    [dispatch, pageSize, searchQuery, transactionTypeQueryValue, sort]
  )

  const { handleScroll } = useEntityInfiniteScroll({
    containerId: 'selectListingScrollContainer',
    itemsLength: listings.length,
    skip,
    fetching,
    loaded,
    hasMore,
    loadPage,
  })

  const handleSearchChange = useCallback(
    (term: string) => {
      const trimmed = term.trim()

      onSearchChange(trimmed)
      if (trimmed === lastTrackedSearchValueRef.current) {
        return
      }

      trackReplySelectSearchUsed({
        entityType: 'listing',
      }, conversationActorRole)
      lastTrackedSearchValueRef.current = trimmed
    },
    [conversationActorRole, onSearchChange]
  )

  const handleTransactionSelect = useCallback(
    (type?: TransactionType) => {
      let nextType: TransactionType | undefined

      if (!type) {
        nextType = undefined
      } else {
        nextType = transactionType === type ? undefined : type
      }

      onTransactionTypeChange(nextType)
      trackReplySelectTransactionTypeChanged({
        entityType: 'listing',
        transactionType: nextType ?? null,
      }, conversationActorRole)
    },
    [conversationActorRole, onTransactionTypeChange, transactionType]
  )

  const handleSortChange = useCallback(
    (nextSort: ListingsSorting) => {
      if (totalListings <= 1) {
        skipNextSortFetchRef.current = true
      }

      onSortChange(nextSort)
      trackReplySelectSortingChanged({
        entityType: 'listing',
        sort: nextSort,
      }, conversationActorRole)
    },
    [conversationActorRole, onSortChange, totalListings]
  )

  const formattedTotalListings = useMemo(
    () => getNumberFormatted(totalListings, culture),
    [culture, totalListings]
  )

  const selectedIdsSet = useMemo(
    () => new Set(selectedIds),
    [selectedIds]
  )

  const listingItems = useMemo(
    () =>
      listings.map((listing) => {
        const cardData = getListingCardData(listing)
        const isSelected = selectedIdsSet.has(listing.id)
        const cardClassName = classNames({
          [styles.listingCardSelected]: isSelected,
        })

        return {
          id: listing.id,
          cardData,
          isSelected,
          cardClassName,
          select: () =>
            onSelect({ id: listing.id, type: 'listing', cardData }),
        }
      }),
    [getListingCardData, listings, onSelect, selectedIdsSet]
  )

  const listingScrollClassName = useMemo(
    () =>
      classNames(styles.listingScroll, {
        [styles.listingScrollHasBar]: hasScrollBar,
      }),
    [hasScrollBar]
  )

  return (
    <div className={styles.listingWrapper}>
      <div className={styles.listingsHeaderContainer}>
        <div className={styles.filters}>
          <div className={styles.searchContainer}>
            <Search onDebouncedChange={handleSearchChange} value={searchQuery} placeholder={SEARCH_PLACEHOLDER} />
          </div>
          <TransactionTypeDropdown
            value={transactionType}
            onChange={handleTransactionSelect}
            className={styles.dropdownWrapper}
          />
        </div>
        <div className={styles.sorting}>
          <div className={styles.listingsCount}>
            {t('Chat.Reply.Select.ListingsCount', {
              count: totalListings,
              formattedTotalListings,
            })}
          </div>
          <div className={styles.sortingDropdownContainer}>
            <SortingDropdown
              value={sort}
              onChange={handleSortChange}
              toggleClassName={styles.sortingDropdownToggle}
            />
          </div>
        </div>
      </div>
      <div
        ref={scrollContainerRef}
        className={listingScrollClassName}
        onScroll={handleScroll}
        id="selectListingScrollContainer"
      >
        <div className={styles.listingContainer}>
          {listingItems.map(({ id, cardData, select, cardClassName }) => (
            <ListingCard
              key={id}
              onClick={select}
              showSubtitle
              className={cardClassName}
              {...cardData}
            />
          ))}
        </div>
        {fetching && <Loader />}
      </div>
    </div>
  )
}
